import * as React from "react";
import { ReactNode, useMemo } from "react";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import * as client from "../../../client";
import {
  CatJobHistoryItem,
  CatResultsResponse,
  PerilModelProfile,
} from "../../../client/types.gen";
import { RmsVersionBadge } from "./RmsVersionBadge";
import { EnrichmentBadge } from "./EnrichmentBadge";
import { formatExactMoneyDollars } from "../Money";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import { useLocalUser } from "../data";
import { formatTimestamp } from "../utils";
import { getPerilColor } from "../icm/util";
import { buildLayerPerilRows } from "./layerPerilRows";
import { RunTags, hasPolicyTermRun } from "./RunTags";
import { BatchLoadStatus } from "./useCatBatchResults";
import {
  EnrichmentSource,
  SelectedModel,
  LayerPerilRow,
  getPerilDisplayName,
  isLayerTotalPeril,
} from "./types";
import styles from "./CatCard.module.css";

// Runs set beside the viewed one, each as its own AAL column.
export interface LayerPerilComparison {
  // The run the table's own columns show; null when it is the active run
  // and the history has no row for it.
  viewedJob: CatJobHistoryItem | null;
  runs: {
    job: CatJobHistoryItem;
    results: CatResultsResponse | null;
    status: BatchLoadStatus;
  }[];
}

interface LayerPerilTabProps {
  selectedModel: SelectedModel;
  // The viewed run's results. Only the selected model's slice is read.
  resultsData: CatResultsResponse | null;
  layerInfoById: Map<string, client.Layer>;
  showAllLayers: boolean;
  onShowAllLayersChange: (value: boolean) => void;
  // When false, the parent renders the "Show all layers" toggle itself.
  renderToggle: boolean;
  // The vendor data the viewed run modeled with, pilled in the toolbar.
  // Undefined while unknown (no pill).
  enrichmentSource: EnrichmentSource | undefined;
  // Run controls for the toolbar's left side. Absent on phones.
  toolbarControls?: ReactNode;
  // Present (possibly with no runs) only where runs can be compared. With at
  // least one run the PML and PMI columns give way to one AAL column per run.
  comparison?: LayerPerilComparison;
}

const MODEL_NAMES: Record<SelectedModel, string> = {
  icm_ylt: "ICM",
  rms_elt: "RMS",
  spatialkat: "KatRisk",
};

const money = (value: number | null | undefined): string =>
  value != null
    ? formatExactMoneyDollars(value, {
        withRounding: true,
        withDollarSign: true,
      })
    : "";

// Signed whole-percent change of ``value`` against ``base``; null when either
// side is missing or the base is zero.
const pctChange = (base: number | null, value: number | null): number | null =>
  base != null && value != null && base !== 0
    ? ((value - base) / Math.abs(base)) * 100
    : null;

// ``status``: a compared run's load state. Its cells show "—" while loading
// and after a failed load, same as for a peril it never modeled, so the
// header has to say which.
const RunColumnHeader: React.FC<{
  label?: string;
  job: CatJobHistoryItem | null;
  showTermTags: boolean;
  status?: BatchLoadStatus;
}> = ({ label, job, showTermTags, status }) => (
  <span className={styles.runColumnHeader}>
    {label && <span>{label}</span>}
    {job && (
      <>
        <span className={styles.runColumnTime}>
          {formatTimestamp(job.started_at)}
        </span>
        <span className={styles.runColumnTags}>
          <RunTags job={job} showTermTags={showTermTags} />
        </span>
      </>
    )}
    {status === "loading" && (
      <span className={styles.runColumnStatus}>
        <i className="pi pi-spin pi-spinner" /> Loading
      </span>
    )}
    {status === "error" && (
      <span className={`${styles.runColumnStatus} ${styles.runColumnError}`}>
        <i className="pi pi-exclamation-circle" /> Failed to load
      </span>
    )}
  </span>
);

export const LayerPerilTab: React.FC<LayerPerilTabProps> = ({
  selectedModel,
  resultsData,
  layerInfoById,
  showAllLayers,
  onShowAllLayersChange,
  renderToggle,
  enrichmentSource,
  toolbarControls,
  comparison,
}) => {
  const comparisonRuns = useMemo(
    () => comparison?.runs ?? [],
    [comparison?.runs]
  );
  const comparing = comparisonRuns.length > 0;

  // RMS model version per peril code, for the version badge. Only the RMS runs
  // report it; other models and older runs leave it empty (no badge).
  const modelProfileByPeril = useMemo(() => {
    const profiles =
      selectedModel === "rms_elt"
        ? resultsData?.rms_elt?.model_profiles
        : undefined;
    const byPeril = new Map<string, PerilModelProfile>();
    for (const profile of profiles ?? []) {
      byPeril.set(profile.peril, profile);
    }
    return byPeril;
  }, [selectedModel, resultsData]);

  // Persisted job-time PMI — ICM only, at 100% of each enabled layer
  // (gross, matching the PML columns; pinned against participation edits).
  // Per-peril figures repeat across that peril's layer rows; the Combined
  // row shows no PMI. Interactive what-ifs live on the Layers tab.
  const user = useLocalUser();
  const pmiEnabled = useFeatureEnabled(
    "portfolio-marginal-impact",
    undefined,
    user
  );
  const pmi =
    pmiEnabled && selectedModel === "icm_ylt" && !comparing
      ? (resultsData?.icm_ylt?.aggregate?.pmi ?? null)
      : null;

  const rows = useMemo(
    (): LayerPerilRow[] =>
      buildLayerPerilRows({
        model: selectedModel,
        primary: resultsData,
        comparisons: comparisonRuns.map((run) => ({
          batchId: run.job.batch_id,
          results: run.results,
        })),
        layerInfoById,
        isLayerVisible: (layerId) =>
          showAllLayers || layerInfoById.get(layerId)?.is_enabled !== false,
        pmi,
      }),
    [
      selectedModel,
      resultsData,
      comparisonRuns,
      layerInfoById,
      showAllLayers,
      pmi,
    ]
  );

  const showTermTags = hasPolicyTermRun([
    ...(comparison?.viewedJob ? [comparison.viewedJob] : []),
    ...comparisonRuns.map((run) => run.job),
  ]);

  const renderPerilLabel = (perilCode: string) => {
    const label = getPerilDisplayName(perilCode);
    if (isLayerTotalPeril(perilCode)) {
      return <span className={styles.fontBold}>{label}</span>;
    }
    return (
      <span className={styles.perilLabel}>
        <span
          className={styles.perilColorDot}
          style={{ backgroundColor: getPerilColor(perilCode) }}
        />
        <span>{label}</span>
        <RmsVersionBadge profile={modelProfileByPeril.get(perilCode)} />
      </span>
    );
  };

  const pmiHeader = (label: string) => (
    <span
      title={
        "Portfolio Marginal Impact at 100% participation (gross, matching " +
        "the AAL and PML columns; participation edits never move this " +
        "figure - use a layer's Portfolio impact view for share-scaled " +
        "figures). Account-wide per peril; PMIs do not sum across perils " +
        "or layers."
      }
    >
      {label}
    </span>
  );

  const moneyCell =
    (field: "pml_100" | "pml_250" | "pml_1000") => (rowData: LayerPerilRow) =>
      money(rowData[field]);

  const pmiCell =
    (field: "pmi_100" | "pmi_250" | "pmi_1000") => (rowData: LayerPerilRow) =>
      money(rowData[field]) || "—";

  // A comparison run's AAL with its change against the viewed run beneath.
  const comparisonCell = (batchId: string) => (rowData: LayerPerilRow) => {
    const value = rowData.aalByBatch[batchId] ?? null;
    if (value == null) return <span className={styles.mutedCell}>—</span>;
    const pct = pctChange(rowData.aal, value);
    return (
      <span className={styles.comparisonCell}>
        <span>{money(value)}</span>
        {pct != null && (
          <span
            className={`${styles.delta} ${
              pct > 0.05
                ? styles.deltaUp
                : pct < -0.05
                  ? styles.deltaDown
                  : styles.deltaFlat
            }`}
          >
            {pct > 0 ? "+" : ""}
            {pct.toFixed(1)}%
          </span>
        )}
      </span>
    );
  };

  const toolbar =
    renderToggle || toolbarControls ? (
      <div className={styles.resultsToolbar}>
        {toolbarControls}
        <div className={styles.resultsToolbarRight}>
          {renderToggle && (
            <label className={styles.layerFilterToggle}>
              <Checkbox
                inputId="show-all-layers"
                checked={showAllLayers}
                onChange={(e) => onShowAllLayersChange(!!e.checked)}
              />
              <span>Show all layers</span>
            </label>
          )}
          {enrichmentSource !== undefined && (
            <EnrichmentBadge source={enrichmentSource} />
          )}
        </div>
      </div>
    ) : null;

  if (rows.length === 0) {
    return (
      <>
        {toolbar}
        <Message
          severity="info"
          text={
            showAllLayers
              ? `No ${MODEL_NAMES[selectedModel]} data available. Run an analysis to generate results.`
              : "No enabled layers to display. Toggle 'Show all layers' to see disabled layers."
          }
          className={styles.messageMargin}
        />
      </>
    );
  }

  return (
    <>
      {toolbar}
      <DataTable
        value={rows}
        rowGroupMode="rowspan"
        groupRowsBy="layer"
        size="small"
        className={styles.resultsTable}
        rowClassName={(rowData: LayerPerilRow) => {
          const classes: string[] = [];
          if (isLayerTotalPeril(rowData.peril)) {
            classes.push(styles.layerTotalRow);
          }
          if (rowData.isFirstInGroup) {
            classes.push(styles.layerGroupStart);
          }
          return classes.join(" ");
        }}
      >
        <Column
          field="layer"
          header="Layer"
          bodyClassName={styles.layerCell}
          headerClassName={styles.layerColumn}
        />
        <Column
          field="peril"
          header="Peril"
          body={(rowData: LayerPerilRow) => renderPerilLabel(rowData.peril)}
          headerClassName={styles.perilColumn}
        />
        <Column
          field="aal"
          header={
            comparing ? (
              <RunColumnHeader
                label="AAL"
                job={comparison?.viewedJob ?? null}
                showTermTags={showTermTags}
              />
            ) : (
              "AAL"
            )
          }
          body={(rowData: LayerPerilRow) => money(rowData.aal)}
          align="right"
          headerClassName={styles.moneyColumn}
          bodyClassName={comparing ? styles.viewedRunCell : undefined}
        />
        {comparisonRuns.map((run) => (
          <Column
            key={run.job.batch_id}
            header={
              <RunColumnHeader
                label="AAL"
                job={run.job}
                showTermTags={showTermTags}
                status={run.status}
              />
            }
            body={comparisonCell(run.job.batch_id)}
            align="right"
            headerClassName={styles.moneyColumn}
          />
        ))}
        {!comparing && (
          <Column
            field="pml_100"
            header="100 Year PML"
            body={moneyCell("pml_100")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {!comparing && (
          <Column
            field="pml_250"
            header="250 Year PML"
            body={moneyCell("pml_250")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {!comparing && (
          <Column
            field="pml_1000"
            header="1000 Year PML"
            body={moneyCell("pml_1000")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {pmi && (
          <Column
            field="pmi_100"
            header={pmiHeader("100 Year PMI")}
            body={pmiCell("pmi_100")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {pmi && (
          <Column
            field="pmi_250"
            header={pmiHeader("250 Year PMI")}
            body={pmiCell("pmi_250")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {pmi && (
          <Column
            field="pmi_1000"
            header={pmiHeader("1000 Year PMI")}
            body={pmiCell("pmi_1000")}
            align="right"
            headerClassName={`${styles.moneyColumn} ${styles.pmlColumn}`}
            bodyClassName={styles.pmlColumn}
          />
        )}
      </DataTable>
      {comparing && (
        <div className={styles.tableFootnote}>
          Change is each run&apos;s AAL against the viewed run&apos;s. PML
          columns return when no runs are compared.
        </div>
      )}
      {pmi && (
        <div className={styles.tableFootnote}>
          All AALs, PMLs, and PMIs assume 100% participation (gross basis). PMI
          (Portfolio Marginal Impact): this account&apos;s contribution to the
          combined book&apos;s PML. Pinned - participation edits never change
          it; each layer&apos;s Portfolio impact view on the Layers tab shows
          the share-scaled figure. Not additive across rows. Book as of{" "}
          {new Date(pmi.as_of).toLocaleString()} · {pmi.portfolio_report_count}{" "}
          in-force account{pmi.portfolio_report_count === 1 ? "" : "s"}.
        </div>
      )}
    </>
  );
};
